import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {apiFetch} from '../api';
import Checklist from './Checklist';
import NewChecklistModal from './NewChecklistModal';
import GroupSidebar from './GroupSidebar';
import TemplateContainer from './TemplateContainer';

const DATE_VIEWS = ['overdue', 'today', 'tomorrow', 'next7days'];

function getLocalDateStr(date) {
    return date.toLocaleDateString('en-CA'); // "YYYY-MM-DD" in local timezone
}

export default function ChecklistContainer() {
    const [checklistData, setChecklistData] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState('all');
    const [showModal, setShowModal] = useState(false);
    const [modalTemplates, setModalTemplates] = useState([]);
    const [focusedId, setFocusedId] = useState(null);
    const [innerView, setInnerView] = useState('checklists');

    useEffect(() => {
        if (!focusedId) return;
        const handleKeyDown = (e) => { if (e.key === 'Escape') setFocusedId(null); };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [focusedId]);

    useEffect(() => {
        document.body.classList.toggle('focus-mode', focusedId !== null);
        return () => document.body.classList.remove('focus-mode');
    }, [focusedId]);

    const focusChecklist = useCallback((id) => setFocusedId(id), []);
    const unfocusChecklist = useCallback(() => setFocusedId(null), []);

    // Ref keeps callbacks stable without stale closure issues
    const dataRef = useRef(checklistData);
    useEffect(() => { dataRef.current = checklistData; }, [checklistData]);

    useEffect(() => {
        const controller = new AbortController();
        apiFetch('/checklists', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setChecklistData(data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        apiFetch('/checklist-groups', {signal: controller.signal})
            .then(res => res.json())
            .then(data => setGroups(data))
            .catch(err => { if (err.name !== 'AbortError') throw err; });
        return () => controller.abort();
    }, []);

    const visibleChecklists = useMemo(() => {
        if (selectedGroupId === 'all') return checklistData;
        if (selectedGroupId === 'starred') {
            return checklistData
                .filter(c => c.items.some(i => i.starred))
                .map(c => ({...c, items: c.items.filter(i => i.starred)}));
        }
        if (DATE_VIEWS.includes(selectedGroupId)) {
            const today = getLocalDateStr(new Date());
            const tomorrow = getLocalDateStr(new Date(Date.now() + 86400000));
            const in7Days = getLocalDateStr(new Date(Date.now() + 7 * 86400000));
            const filters = {
                overdue:   i => !i.checked && i.dueDate && i.dueDate < today,
                today:     i => !i.checked && i.dueDate === today,
                tomorrow:  i => !i.checked && i.dueDate === tomorrow,
                next7days: i => !i.checked && i.dueDate > tomorrow && i.dueDate <= in7Days,
            };
            const fn = filters[selectedGroupId];
            return checklistData
                .filter(c => c.items.some(fn))
                .map(c => ({...c, items: c.items.filter(fn)}));
        }
        if (selectedGroupId === null) return checklistData.filter(c => c.groupId == null);
        return checklistData.filter(c => c.groupId === selectedGroupId);
    }, [checklistData, selectedGroupId]);

    const putChecklist = useCallback(async (checklistId, updated) => {
        const res = await apiFetch(`/checklists/${checklistId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(updated),
        });
        const saved = await res.json();
        setChecklistData(prev => prev.map(c => c.id === checklistId ? saved : c));
    }, []);

    const deleteChecklist = useCallback(async (id) => {
        await apiFetch(`/checklists/${id}`, {method: 'DELETE'});
        setChecklistData(prev => prev.filter(c => c.id !== id));
        setFocusedId(prev => prev === id ? null : prev);
    }, []);

    const updateTitle = useCallback((checklistId, title) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        putChecklist(checklistId, {...checklist, title});
    }, [putChecklist]);

    const updateItem = useCallback(async (checklistId, itemId, title) => {
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const deleteItem = useCallback(async (checklistId, itemId) => {
        await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {method: 'DELETE'});
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.filter(i => i.id !== itemId)} : c
        ));
    }, []);

    const addItem = useCallback(async (checklistId, title) => {
        const res = await apiFetch(`/checklists/${checklistId}/items`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({title}),
        });
        const newItem = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: [newItem, ...c.items]} : c
        ));
    }, []);

    const starItem = useCallback(async (checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const item = checklist.items.find(i => i.id === itemId);
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({starred: !item.starred}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const setDueDate = useCallback(async (checklistId, itemId, dueDate) => {
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({dueDate}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const reorderItems = useCallback((checklistId, movedItemId, afterId, newItems) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const updated = {...checklist, items: newItems, sortMode: 'custom'};
        setChecklistData(prev => prev.map(c => c.id === checklistId ? updated : c));
        apiFetch(`/checklists/${checklistId}/items/${movedItemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({after: afterId}),
        });
        if (checklist.sortMode !== 'custom') {
            apiFetch(`/checklists/${checklistId}`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({...checklist, sortMode: 'custom'}),
            });
        }
    }, []);

    const toggleSortMode = useCallback((checklistId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        if (checklist.sortMode === 'custom') {
            putChecklist(checklistId, {...checklist, sortMode: 'created_at'});
        } else {
            const sorted = [...checklist.items].sort((a, b) => a.position - b.position);
            putChecklist(checklistId, {...checklist, items: sorted, sortMode: 'custom'});
        }
    }, [putChecklist]);

    const toggleItem = useCallback(async (checklistId, itemId) => {
        const checklist = dataRef.current.find(c => c.id === checklistId);
        const item = checklist.items.find(i => i.id === itemId);
        const res = await apiFetch(`/checklists/${checklistId}/items/${itemId}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({checked: !item.checked}),
        });
        const updated = await res.json();
        setChecklistData(prev => prev.map(c =>
            c.id === checklistId ? {...c, items: c.items.map(i => i.id === itemId ? updated : i)} : c
        ));
    }, []);

    const addNewChecklist = useCallback(async (title) => {
        const body = {title, items: []};
        if (selectedGroupId !== 'all') {
            body.groupId = selectedGroupId; // null (Default) or a group UUID string
        }
        const res = await apiFetch('/checklists', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(body),
        });
        const created = await res.json();
        setChecklistData(prev => [...prev, created]);
        setFocusedId(created.id);
    }, [selectedGroupId]);

    const openNewListModal = useCallback(async () => {
        const res = await apiFetch('/checklist-templates');
        const templates = await res.json();
        setModalTemplates(templates);
        setShowModal(true);
    }, []);

    const handleModalConfirm = useCallback(async (title, templateId) => {
        setShowModal(false);
        if (templateId) {
            const res = await apiFetch(`/checklist-templates/${templateId}/spawn`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({name: title}),
            });
            const created = await res.json();
            setChecklistData(prev => [...prev, created]);
            setFocusedId(created.id);
        } else {
            addNewChecklist(title);
        }
    }, [addNewChecklist]);

    const renameGroup = useCallback(async (id, name) => {
        await apiFetch(`/checklist-groups/${id}`, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        setGroups(prev => prev.map(g => g.id === id ? {...g, name} : g));
    }, []);

    const createGroup = useCallback(async (name) => {
        const res = await apiFetch('/checklist-groups', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name}),
        });
        const group = await res.json();
        setGroups(prev => [...prev, group]);
    }, []);

    const deleteGroup = useCallback(async (id) => {
        const res = await apiFetch(`/checklist-groups/${id}`, {method: 'DELETE'});
        if (!res.ok) return;
        setGroups(prev => prev.filter(g => g.id !== id));
        setSelectedGroupId(prev => prev === id ? 'all' : prev);
    }, []);

    return (
        <div className="checklist-layout">
            <GroupSidebar
                groups={groups}
                selectedGroupId={selectedGroupId}
                checklistData={checklistData}
                onSelect={(id) => { setInnerView('checklists'); setSelectedGroupId(id); }}
                onAddGroup={createGroup}
                onRenameGroup={renameGroup}
                onDeleteGroup={deleteGroup}
                onTemplates={() => setInnerView('templates')}
                isTemplatesActive={innerView === 'templates'}
                hasDueDateItems={checklistData.some(c => c.items.some(i => i.dueDate))}
            />
            {innerView === 'templates' ? (
                <TemplateContainer onSpawn={(checklist) => {
                    setChecklistData(prev => [...prev, checklist]);
                    setInnerView('checklists');
                    setSelectedGroupId('all');
                }} />
            ) : (
                <div className="checklist-container-root">
                    <div>
                        <button className="add-checklist-button" onClick={openNewListModal}>
                            + New List
                        </button>
                    </div>
                    {showModal ? (
                        <NewChecklistModal onConfirm={handleModalConfirm} onCancel={() => setShowModal(false)} templates={modalTemplates}/>
                    ) : null}
                    {focusedId !== null && (
                        <div className="focus-backdrop" onClick={() => setFocusedId(null)} />
                    )}
                    <div className={`checklists-container${focusedId !== null ? ' checklists-container--hidden' : ''}`}>
                        {visibleChecklists.map(checklist => (
                            <Checklist
                                key={checklist.id}
                                data={checklist}
                                onDelete={deleteChecklist}
                                onAddItem={addItem}
                                onToggleItem={toggleItem}
                                onDeleteItem={deleteItem}
                                onUpdateTitle={updateTitle}
                                onUpdateItem={updateItem}
                                onReorderItems={reorderItems}
                                onToggleSortMode={toggleSortMode}
                                onStarItem={starItem}
                                onSetDueDateItem={setDueDate}
                                readOnly={selectedGroupId === 'starred' || DATE_VIEWS.includes(selectedGroupId)}
                                isFocused={focusedId === checklist.id}
                                onFocus={focusChecklist}
                                onUnfocus={unfocusChecklist}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
